import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

export const CartContext = createContext(null);

// Egyszerű session ID generálás (perzisztens a sessionben)
function getSessionId() {
  let sid = sessionStorage.getItem('cruiser_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('cruiser_sid', sid);
  }
  return sid;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cruiser_cart') || '[]');
    } catch {
      return [];
    }
  });

  const sessionId = useRef(getSessionId());

  /* localStorage szinkron */
  useEffect(() => {
    localStorage.setItem('cruiser_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  // Kosár esemény küldése a backendnek (aszinkron, nem blokkol)
  const trackCartEvent = useCallback((product_id, quantity) => {
    api('/api/cart/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id, quantity, session_id: sessionId.current }),
    }).catch(() => {}); // silent fail
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    // Elektromos kategória blokkolva (dupla védelem)
    if (product.category_slug === 'elektromos' || product.category === 'elektromos') return;

    setCartItems(prev => {
      const exists = prev.find(i => i.id === product.id);
      if (exists) {
        return prev.map(i =>
          i.id === product.id
            ? { ...i, quantity: Math.min(i.quantity + quantity, product.stock || 99) }
            : i
        );
      }
      return [...prev, {
        id:        product.id,
        slug:      product.slug,
        name:      product.name,
        price:     product.price,
        image_url: product.image_url,
        stock:     product.stock,
        quantity,
      }];
    });

    // Tracking
    trackCartEvent(product.id, quantity);
  }, [trackCartEvent]);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) return removeFromCart(id);
    setCartItems(prev =>
      prev.map(i => i.id === id ? { ...i, quantity } : i)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFromCart = useCallback((id) => {
    setCartItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  return (
    <CartContext.Provider value={{
      cartItems, cartCount, cartTotal,
      addToCart, updateQuantity, removeFromCart, clearCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}